import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'cva';
import { mergeProps } from '@base-ui/react/merge-props';
import { cn } from '@/ui/utils';

const _BADGE_DEFAULT_TAG = 'span' satisfies React.ElementType;

const badge = cva({
  base: [
    'inline-flex items-center shadow-xs justify-center whitespace-nowrap [&>svg]:shrink-0 shrink-0',
  ],
  variants: {
    variant: {
      default: 'bg-white border',
      secondary: 'bg-surface-3 border',
      tertiary: 'bg-surface-3 shadow-none',
      primary: 'bg-primary-11/11 shadow-none text-primary-12',
      'tertiary-accent': 'bg-primary-3 shadow-none text-primary-11',
      'tertiary-blue': 'bg-blue-3 shadow-none text-blue-11',
      'tertiary-success': 'bg-green-3 shadow-none text-green-11',
      'tertiary-warning': 'bg-orange-3 shadow-none text-orange-11',
      'tertiary-warning-2': 'bg-amber-3 shadow-none text-amber-11',
      'tertiary-destructive': 'bg-red-3 shadow-none text-red-11',
      destructive: 'bg-red-3 border border-neutral text-red-11',
      warning: 'bg-orange-3 border border-neutral text-orange-11',
      'warning-2': 'bg-amber-3 border border-neutral text-amber-11',
      success: 'bg-green-3 border border-neutral text-green-11',
    },
    size: {
      xxs: 'h-5 px-1 gap-1 text-xs rounded-sm [&_svg:not([class*="size-"])]:size-3.5',
      xs: 'h-6 px-1.5 gap-1 text-xs rounded-md [&_svg:not([class*="size-"])]:size-4',
      sm: 'h-7 px-1.75 gap-1.5 text-[0.8rem] rounded-md [&_svg:not([class*="size-"])]:size-4',
      md: 'h-8 px-2 gap-2 text-sm rounded-md [&_svg:not([class*="size-"])]:size-4.5',
      lg: 'h-9 px-2.5 gap-2 text-sm rounded-md [&_svg:not([class*="size-"])]:size-5',
    },
    kind: {
      default: '',
      icon: 'px-0! aspect-square w-auto justify-center',
      'with-icon': '',
      count: 'size-4 text-[10px] rounded-sm group-[[aria-current=page]]:border-surface-5',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    kind: 'default',
  },
  compoundVariants: [
    {
      size: 'xs',
      kind: 'with-icon',
      className: 'gap-1.5 [&>svg:first-child]:-mx-0.5 [&>svg:last-child]:-mx-0.5',
    },
    {
      size: 'sm',
      kind: 'with-icon',
      className: 'gap-2 [&>svg:first-child]:-mx-0.5 [&>svg:last-child]:-mx-0.5',
    },
    {
      size: 'md',
      kind: 'with-icon',
      className: 'gap-2.5 [&>svg:first-child]:-mx-0.75 [&>svg:last-child]:-mx-0.75',
    },
    {
      size: 'lg',
      kind: 'with-icon',
      className: 'gap-3 [&>svg:first-child]:-mx-0.5 [&>svg:last-child]:-mx-0.5',
    },
  ],
});

interface Props
  extends useRender.ComponentProps<typeof _BADGE_DEFAULT_TAG>,
    VariantProps<typeof badge> {}

export function Badge({ render, className, kind, variant, size, ...props }: Props) {
  const defaultProps: useRender.ElementProps<typeof _BADGE_DEFAULT_TAG> = {
    className: cn(badge({ variant, size, kind, className })),
    ['data-slot' as string]: 'badge',
  };

  return useRender({
    defaultTagName: _BADGE_DEFAULT_TAG,
    render,
    props: mergeProps<typeof _BADGE_DEFAULT_TAG>(defaultProps, props),
  });
}

const badgeDot = cva({
  base: ['inline-block shrink-0'],
  variants: {
    variant: {
      default: 'bg-background border',
      muted: 'bg-surface-10',
      neutral: 'bg-surface-5 border',
      'neutral-light': 'bg-surface-3 border',
      primary: 'bg-primary-12',
      blue: 'bg-blue-10',
      success: 'bg-success',
      warning: 'bg-warning',
      'warning-2': 'bg-amber-11',
      destructive: 'bg-destructive',
    },
    size: {
      md: 'size-2 rounded-[3px]',
      lg: 'size-2.5 rounded-[3px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

interface BadgeDotProps extends React.ComponentProps<'span'>, VariantProps<typeof badgeDot> {}

export function BadgeDot({ className, variant, size, ...props }: BadgeDotProps) {
  return <span className={cn(badgeDot({ variant, size, className }))} {...props}></span>;
}
